import { createContext, useCallback, useContext, useState } from "react";
import { createPortal } from "react-dom";

/**
 * SPEC 4.8 / 5.8:
 *  - Bottom-of-screen ephemeral confirmation.
 *  - Optional undo button + 5s auto-dismiss.
 *  - Live region for screen readers.
 *  - Respects prefers-reduced-motion (CSS).
 */

type Toast = {
  id: number;
  message: string;
  undo?: { label: string; onUndo: () => void };
  durationMs: number;
};

type ToastContextValue = {
  show: (
    message: string,
    options?: { durationMs?: number; undo?: Toast["undo"] },
  ) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback<ToastContextValue["show"]>((message, options) => {
    const id = nextId++;
    const t: Toast = {
      id,
      message,
      undo: options?.undo,
      durationMs: options?.durationMs ?? 5000,
    };
    setToasts((curr) => [...curr, t]);
    window.setTimeout(() => {
      setToasts((curr) => curr.filter((x) => x.id !== id));
    }, t.durationMs);
  }, []);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      {typeof document !== "undefined" &&
        createPortal(
          <ol
            aria-live="polite"
            aria-atomic="false"
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: "calc(var(--space-6) + env(safe-area-inset-bottom))",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--space-2)",
              listStyle: "none",
              margin: 0,
              padding: 0,
              pointerEvents: "none",
              zIndex: 60,
            }}
          >
            {toasts.map((t) => (
              <ToastItem
                key={t.id}
                toast={t}
                onDismiss={() =>
                  setToasts((curr) => curr.filter((x) => x.id !== t.id))
                }
              />
            ))}
          </ol>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  return (
    <li
      style={{
        pointerEvents: "auto",
        background: "var(--bg-3)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-3) var(--space-4)",
        color: "var(--fg)",
        fontSize: "var(--type-body-s)",
        display: "flex",
        alignItems: "center",
        gap: "var(--space-4)",
        minWidth: 280,
        maxWidth: 480,
        position: "relative",
        overflow: "hidden",
        animation: `haus-toast-in var(--dur-base) var(--ease-out)`,
      }}
    >
      <span style={{ flex: 1 }}>{toast.message}</span>
      {toast.undo && (
        <button
          type="button"
          onClick={() => {
            toast.undo!.onUndo();
            onDismiss();
          }}
          style={{
            color: "var(--accent)",
            fontWeight: 500,
            fontSize: "var(--type-body-s)",
            padding: "var(--space-1) var(--space-2)",
            border: "1px solid transparent",
            borderRadius: "var(--radius-chip)",
          }}
        >
          {toast.undo.label}
        </button>
      )}
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 1,
          background: "var(--accent)",
          transformOrigin: "left",
          animation: `haus-toast-progress ${toast.durationMs}ms linear forwards`,
        }}
      />
      <style>{`
        @keyframes haus-toast-in {
          from { opacity: 0; transform: translateY(8px) }
          to   { opacity: 1; transform: translateY(0)   }
        }
        @keyframes haus-toast-progress {
          from { transform: scaleX(1) } to { transform: scaleX(0) }
        }
      `}</style>
    </li>
  );
}
